import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/slices/notificationPopupSlice';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import Toast from '../Toast';
import { playNotificationSound } from '../../utils/notificationSound';

const WaiterNotifications = ({ waiterId, restaurantId }) => {
    const dispatch = useDispatch();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [filter, setFilter] = useState('pending'); // 'pending', 'all', 'done'
    const lastNotificationIdsRef = useRef(new Set());

    useEffect(() => {
        if (waiterId && restaurantId) {
            fetchNotifications();
            // Set up polling to refresh notifications every 5 seconds
            const interval = setInterval(fetchNotifications, 5000);
            return () => clearInterval(interval);
        }
    }, [waiterId, restaurantId, filter]);

    const fetchNotifications = async () => {
        try {
            const statusParam = filter === 'pending' ? 'pending' : filter === 'all' ? undefined : filter;
            const response = await axios.get(API_ENDPOINTS.WAITER_REQUESTS_BY_WAITER(waiterId, restaurantId), {
                params: statusParam ? { status: statusParam } : {}
            });
            const newNotifications = response.data || [];
            
            // Check for new notifications (especially PICKUP type from chef)
            if (newNotifications.length > 0) {
                const currentNotificationIds = new Set(newNotifications.map(n => n._id));
                const previousIds = lastNotificationIdsRef.current;
                
                // Find new notifications
                const newNotificationsList = newNotifications.filter(n => !previousIds.has(n._id));
                
                // Show pop-up and play sound for new notifications
                if (newNotificationsList.length > 0) {
                    const first = newNotificationsList[0];
                    const title = first.requestType === 'PICKUP' ? 'Order Ready for Pickup' : 'New Waiter Request';
                    const message = first.message || (first.requestType === 'PICKUP' ? 'Kitchen has an order ready for pickup.' : 'You have a new request.');
                    playNotificationSound();
                    dispatch(showNotification({ title, message }));
                }
                
                lastNotificationIdsRef.current = currentNotificationIds;
            }
            
            setNotifications(newNotifications);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
            showToast('Failed to load notifications', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleUpdateStatus = async (requestId, newStatus, reply = '') => {
        try {
            await axios.put(API_ENDPOINTS.WAITER_REQUEST_UPDATE(requestId), {
                status: newStatus,
                reply: reply
            });
            showToast('Request updated successfully', 'success');
            fetchNotifications();
        } catch (error) {
            console.error('Failed to update request:', error);
            showToast(error.response?.data?.message || 'Failed to update request', 'error');
        }
    };

    const getRequestTypeIcon = (requestType) => {
        switch (requestType) {
            case 'PICKUP':
                return '🍽️';
            case 'CUSTOMER_REQUEST':
                return '🔔';
            case 'KITCHEN_NOTE':
                return '📝';
            default:
                return '📋';
        }
    };

    const getRequestTypeLabel = (requestType) => {
        switch (requestType) {
            case 'PICKUP':
                return 'Order Ready for Pickup';
            case 'CUSTOMER_REQUEST':
                return 'Customer Request';
            case 'KITCHEN_NOTE':
                return 'Kitchen Note';
            default:
                return 'Request';
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'initiated': { label: 'New', color: 'bg-blue-100 text-blue-800' },
            'process': { label: 'In Process', color: 'bg-yellow-100 text-yellow-800' },
            'done': { label: 'Completed', color: 'bg-green-100 text-green-800' }
        };
        const config = statusConfig[status] || statusConfig['initiated'];
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
                {config.label}
            </span>
        );
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        return date.toLocaleDateString();
    };

    const pendingNotifications = notifications.filter(n => n.status === 'initiated' || n.status === 'process');
    const pendingCount = pendingNotifications.length;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header with filter and count */}
                            <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold text-gray-800">Notifications</h2>
                    {pendingCount > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                            {pendingCount} New
                        </span>
                    )}
                </div>
            <div className="flex items-center justify-between mb-4">

                <div className="flex gap-2">
                    <button
                        onClick={() => setFilter('pending')}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            filter === 'pending'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        Pending
                    </button>
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            filter === 'all'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setFilter('done')}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            filter === 'done'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        Completed
                    </button>
                </div>
            </div>

            {/* Notifications List */}
            {notifications.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <div className="text-4xl mb-4">🔕</div>
                    <p className="text-gray-500">No notifications found</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {notifications.map((notification) => (
                        <div
                            key={notification._id}
                            className={`bg-white rounded-lg shadow-md p-4 border-l-4 ${
                                notification.status === 'initiated'
                                    ? 'border-blue-500'
                                    : notification.status === 'process'
                                    ? 'border-yellow-500'
                                    : 'border-green-500'
                            }`}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex items-start gap-3 flex-1">
                                    <div className="text-2xl">
                                        {getRequestTypeIcon(notification.requestType)}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-semibold text-gray-800">
                                                {getRequestTypeLabel(notification.requestType)}
                                            </h3>
                                            {getStatusBadge(notification.status)}
                                        </div>
                                        <p className="text-gray-600 text-sm mb-2">
                                            {notification.message}
                                        </p>
                                        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                                            {notification.tableId && (
                                                <span>📍 Table {notification.tableId.tableNumber}</span>
                                            )}
                                            {notification.orderId && (
                                                <span>📦 Order #{notification.orderId._id?.toString().slice(-6) || 'N/A'}</span>
                                            )}
                                            <span>👤 {notification.customerName}</span>
                                            <span>🕐 {formatTime(notification.createdAt)}</span>
                                        </div>
                                        {notification.requestedBy && (
                                            <p className="text-xs text-gray-400 mt-1">
                                                Requested by: {notification.requestedBy.name || 'Kitchen'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            {notification.status !== 'done' && (
                                <div className="flex gap-2 mt-3 pt-3 border-t">
                                    {notification.status === 'initiated' && (
                                        <>
                                            <button
                                                onClick={() => handleUpdateStatus(notification._id, 'process')}
                                                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                            >
                                                Mark as In Process
                                            </button>
                                            <button
                                                onClick={() => handleUpdateStatus(notification._id, 'done')}
                                                className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                            >
                                                Mark as Done
                                            </button>
                                        </>
                                    )}
                                    {notification.status === 'process' && (
                                        <button
                                            onClick={() => handleUpdateStatus(notification._id, 'done')}
                                            className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                        >
                                            Mark as Done
                                        </button>
                                    )}
                                </div>
                            )}

                            {notification.reply && (
                                <div className="mt-2 pt-2 border-t">
                                    <p className="text-sm text-gray-600">
                                        <span className="font-semibold">Your reply:</span> {notification.reply}
                                    </p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {toast.show && <Toast message={toast.message} type={toast.type} />}
            
        </div>
    );
};

export default WaiterNotifications;
